/* eslint-disable boundaries/element-types */
import { DrawerState } from 'components/rightDrawer/utils/enums'
import { useRolodexEntries } from 'features/rolodex'
import { useEffect, useMemo, useState } from 'react'
import { RolodexEntryType } from 'types'

/**
 * A hook which uses useRolodexEntries() to load data (units, site, jobs,
 * etc.) relevant to a given site ID, supplied from the
 * current route.
 * @param props       The site ID to load relevant data for
 */
export const useSiteRolodexEntries = (props: {
  siteID: string | undefined
  drawerState: DrawerState
}) => {
  //----------------------------------------------------------------------------
  const { siteID, drawerState } = props

  const {
    sites,
    organizations,
    getSite,
    getOrganization,
    getInspection,
    forceRefresh,
  } = useRolodexEntries({
    type: [RolodexEntryType.SITE, RolodexEntryType.ORGANIZATION],
    order_by: [{ key: 'name', descending: 'false' }],
  })

  const { site, organization } = useMemo(() => {
    const site = siteID
      ? getSite(siteID)
      : (getSite('atlantic-deer-island') ?? sites.at(0))

    const siteOrganization = site?.tags.organization

    const organization = getOrganization(site ? siteOrganization : siteID)
    return { site, organization }
  }, [siteID, sites, getSite, getOrganization])

  const {
    jobs,
    units,
    components,
    forceRefresh: secondForceRefresh,
  } = useRolodexEntries(
    !site
      ? []
      : {
          type: [
            RolodexEntryType.JOB,
            RolodexEntryType.UNIT,
            RolodexEntryType.COMPONENT,
          ],
          tags: { site: site.id },
        }
  )

  const [forceRefreshData, setForceRefreshData] = useState(false)

  useEffect(() => {
    // A state of interest has been opened, let's prep the refresh
    if (
      drawerState === DrawerState.ImportGeckoInspection &&
      !forceRefreshData
    ) {
      setForceRefreshData(true)
    }
    // The drawer has now been closed (after being in a state of interest), refresh the data and reset the variable
    if (drawerState === DrawerState.Closed && forceRefreshData) {
      forceRefresh()
      secondForceRefresh()
      setForceRefreshData(false)
    }
  }, [drawerState, forceRefreshData, forceRefresh, secondForceRefresh])

  return {
    organization,
    organizations,
    site,
    sites,
    jobs,
    units,
    components,
    getInspection,
    forceRefresh,
  }
}
